/**
 * Jogger offers: the hoodie-or-set cards on the PDP (set_choice block).
 * Set lines are written exactly like the legacy bundle box (confirmBundle in alpine-data.js):
 * __glo_bundle groups them in the drawer and __winter_bundle_sale_fixed_price carries the
 * price built from each product's glo_bundle_discount metafield.
 */
document.addEventListener('alpine:init', () => {
  let setChoiceCtx = null;

  const lastSegment = (value) => (value || '').toString().split('-').pop().trim().toLowerCase();
  const isAvailable = (variant) => !!variant && (variant.remaining_quantity > 0 || variant.inventory_policy == 'continue');
  const discountCents = (variant) => Math.round(parseFloat(variant?.metafields?.glo_bundle_discount_value || 0) * 100);
  const withWidth = (url, width) => url ? `${url}${url.includes('?') ? '&' : '?'}width=${width}` : '';

  function optionKey(product, name) {
    const option = product.options.find((o) => o.name.toLowerCase() == name);
    return option ? `option${option.position}` : null;
  }

  // Same rule as productTile.syncBundleColor: exact name first, then the last segment ("Forgiven - True Red" → "True Red")
  function matchColor(product, color) {
    const colorKey = optionKey(product, 'color');
    if (!colorKey || !color) return null;
    const soldOut = product.sold_out_color_names || [];
    const values = [...new Set(product.variants.map((v) => v[colorKey]))].filter((c) => !soldOut.includes(c));
    return values.find((c) => c == color) || values.find((c) => lastSegment(c) == lastSegment(color)) || null;
  }

  function availableColors(product) {
    const colorKey = optionKey(product, 'color');
    if (!colorKey) return [];
    const soldOut = product.sold_out_color_names || [];
    return [...new Set(product.variants.filter(isAvailable).map((v) => v[colorKey]))].filter((c) => !soldOut.includes(c));
  }

  function findVariant(product, color, size) {
    const colorKey = optionKey(product, 'color');
    const sizeKey = optionKey(product, 'size');
    return product.variants.find((v) => (!colorKey || v[colorKey] == color) && (!sizeKey || v[sizeKey] == size));
  }

  function lastBundleIndex() {
    return Alpine.store('cart').cartItems
      .filter((i) => i.properties.attributes.__glo_bundle)
      .reduce((last, i) => Math.max(last, i.properties.attributes.__glo_bundle.bundleIndex), -1);
  }

  function eddProperties() {
    const { eddDateText, daysInTransit } = Alpine.store('edd') || {};
    return eddDateText && daysInTransit ? { __estimated_delivery_date: eddDateText } : {};
  }

  // secondary_atc_discounted_price is in dollars; pieces without one keep their price
  function secondaryPrice(variant) {
    const price = parseFloat(variant.metafields?.secondary_atc_discounted_price || 0);
    return price ? Math.round(price * 100) : variant.price;
  }

  function money(cents) {
    return Alpine.store('currency').formatCartMoney(cents, cents % 100 == 0 ? 0 : 2);
  }

  function colorSwatch(color) {
    const cart = Alpine.store('cart');
    const image = cart.getColorSwatchByName(color);
    if (image) return `background-image:url(${image});background-size:cover`;
    return `background-color:${cart.getColorHaxByName(color) || '#d9d6cf'}`;
  }

  function fixedPrice(price, saleName = 'Kingdom Essential Bundle') {
    return { __winter_bundle_sale_fixed_price: { type: 'FIXED', price, saleName } };
  }

  Alpine.store('gloSet', {
    active: false,
    choice: 'hoodie',
    // Mirrored from the set_choice block for the buy buttons, which sit outside it
    addOnReady: false,
    setPrice: 0,
    separatePrice: 0,
    secondarySetPrice: 0,
    preOrderSetPrice: 0,

    get isSet() {
      return this.active && this.choice == 'set';
    },

    add(mode) {
      if (setChoiceCtx) setChoiceCtx.addSet(mode);
    },
  });

  Alpine.data('gloSetChoice', (blockId) => ({
    cfg: {},
    addOns: [],
    addOnIndex: 0,
    addOnColor: null,
    addOnSize: null,
    colorsOpen: false,

    init() {
      const data = JSON.parse(document.getElementById(`GloSetChoice-${blockId}`).textContent);
      this.cfg = data.settings;
      this.addOns = data.addOns;

      setChoiceCtx = this;
      const store = Alpine.store('gloSet');
      store.active = true;
      store.choice = this.cfg.defaultChoice == 'set' ? 'set' : 'hoodie';

      this.matchHoodie();
      this.$watch('hoodieColor', () => this.matchHoodie());
      this.$watch('hoodieSize', () => this.matchHoodieSize());
      this.$watch('addOnIndex', () => this.matchHoodie());
    },

    // Publishes what the buy buttons need; runs from x-effect on the block root
    publish() {
      const store = Alpine.store('gloSet');
      store.addOnReady = this.addOnReady;
      store.setPrice = this.setPrice;
      store.separatePrice = this.separatePrice;
      store.secondarySetPrice = this.secondarySetPrice;
      store.preOrderSetPrice = this.preOrderSetPrice;
    },

    get addOn() {
      return this.addOns[this.addOnIndex];
    },

    get setTitle() {
      return this.cfg.setLabel.replace('[add_on]', this.addOn ? this.addOn.label : '');
    },

    get hoodieColor() {
      const key = this.selectedVariant && optionKey(this.product, 'color');
      return key ? this.selectedVariant[key] : null;
    },

    get hoodieSize() {
      const key = this.selectedVariant && optionKey(this.product, 'size');
      return key ? this.selectedVariant[key] : null;
    },

    matchHoodie() {
      if (!this.addOn) return;
      const product = this.addOn.product;
      this.addOnColor = matchColor(product, this.hoodieColor) || matchColor(product, this.addOnColor) || availableColors(product)[0];
      this.matchHoodieSize();
    },

    // Joggers follow the hoodie size; the size buttons in the card can override it
    matchHoodieSize() {
      if (!this.addOn) return;
      const sizes = this.addOnSizes;
      if (sizes.includes(this.hoodieSize)) {
        this.addOnSize = this.hoodieSize;
      } else if (!sizes.includes(this.addOnSize)) {
        this.addOnSize = sizes[0] || null;
      }

      // check if selected variant is OOS
      if (!isAvailable(this.addOnVariant)) {
        // Get first selected size
        const availableSize = this.addOnSizes.find(size => this.sizeAvailable(size));
        if (availableSize) {
          this.addOnSize = availableSize
        }
      }

    },

    get addOnSizes() {
      const sizeKey = this.addOn && optionKey(this.addOn.product, 'size');
      return sizeKey ? [...new Set(this.addOn.product.variants.map((v) => v[sizeKey]))] : [];
    },

    get addOnColors() {
      return this.addOn ? availableColors(this.addOn.product) : [];
    },

    sizeAvailable(size) {
      return isAvailable(findVariant(this.addOn.product, this.addOnColor, size));
    },

    get addOnVariant() {
      return this.addOn ? findVariant(this.addOn.product, this.addOnColor, this.addOnSize) : null;
    },

    get addOnReady() {
      return isAvailable(this.addOnVariant);
    },

    get colorSoldOut() {
      return !!this.addOn && !this.addOnSizes.some((size) => this.sizeAvailable(size));
    },

    // Colour swatches show under "Change", or by themselves when no size is left in the colour
    get colorsShown() {
      return Alpine.store('gloSet').isSet && (this.colorsOpen || this.colorSoldOut);
    },

    // Any variant in the chosen colour, for price and photo while the size is unavailable
    get addOnDisplayVariant() {
      if (!this.addOn) {
        return null;
      }
      if (this.addOnVariant) return this.addOnVariant;
      const colorKey = optionKey(this.addOn.product, 'color');
      return this.addOn.product.variants.find((v) => !colorKey || v[colorKey] == this.addOnColor) || this.addOn.product.variants[0];
    },

    get addOnImage() {
      if (!this.addOn) {
        return null;
      }
      return this.addOn.image || withWidth(this.addOnDisplayVariant.image || this.addOn.product.image, 300);
    },

    get addOnImageLarge() {
      if (!this.addOn) {
        return null;
      }
      return this.addOn.imageLarge || withWidth(this.addOnDisplayVariant.image || this.addOn.product.image, 1000);
    },

    get hoodieImage() {
      if (!this.selectedVariant) {
        return null;
      }
      return withWidth((this.selectedVariant && this.selectedVariant.image) || this.product.image, 300);
    },

    // "Forgiven - True Red" + M → "Forgiven · True Red · M", as the bag lists a line's options
    get hoodieMeta() {
      return [(this.hoodieColor || '').split(' - ').join(' · '), this.hoodieSize].filter(Boolean).join(' · ');
    },

    get addOnMeta() {
      return [this.addOnColor, this.addOnSize].filter(Boolean).join(' · ');
    },

    get hoodiePrice() {
      return this.selectedVariant ? this.selectedVariant.price : 0;
    },

    get addOnPrice() {
      return this.addOn ? this.addOnDisplayVariant.price : 0;
    },

    // Each line minus its own glo_bundle_discount, the same prices the bag charges for a set
    get hoodieSetPrice() {
      return this.hoodiePrice - discountCents(this.selectedVariant);
    },

    get addOnSetPrice() {
      return this.addOnPrice - (this.addOn ? discountCents(this.addOnDisplayVariant) : 0);
    },

    get separatePrice() {
      return this.hoodiePrice + this.addOnPrice;
    },

    get setPrice() {
      return this.hoodieSetPrice + this.addOnSetPrice;
    },

    get setDiscount() {
      return this.separatePrice - this.setPrice;
    },

    get secondarySetPrice() {
      if (!this.selectedVariant || !this.addOn) return 0;
      return secondaryPrice(this.selectedVariant) + secondaryPrice(this.addOnDisplayVariant);
    },

    // Hoodie on pre-order, add-on shipped now: no set discount, as in the legacy hybrid bundle
    get preOrderSetPrice() {
      if (!this.selectedPreOrderVariant || !this.addOn) return 0;
      return Math.round(parseFloat(this.selectedPreOrderVariant.price) * 100) + this.addOnDisplayVariant.price;
    },

    money,
    colorSwatch,

    chooseSet() {
      Alpine.store('gloSet').choice = 'set';
    },

    addSet(mode) {
      if (!this.addOnReady) return;

      const bundleIndex = lastBundleIndex() + 1;
      const shared = { __glo_bundle: { bundleIndex }, ...eddProperties() };
      let hoodie;
      let addOn;

      if (mode == 'preorder') {
        hoodie = {
          id: this.selectedPreOrderVariant.id,
          properties: { ...shared, ...fixedPrice(Math.round(parseFloat(this.selectedPreOrderVariant.price) * 100)), PreOrder: this.selectedPreOrderVariant.est_date },
        };
        addOn = { id: this.addOnVariant.id, properties: { ...shared, ...fixedPrice(this.addOnVariant.price) } };
      } else if (mode == 'secondary') {
        const secondary = { ...shared, SecodaryOrderEST: this.secondaryAtcEdd };
        hoodie = { id: this.selectedVariant.id, properties: { ...secondary, ...fixedPrice(secondaryPrice(this.selectedVariant), 'Kingdom Essential Pre-Order Bundle') } };
        addOn = { id: this.addOnVariant.id, properties: { ...secondary, ...fixedPrice(secondaryPrice(this.addOnVariant), 'Kingdom Essential Pre-Order Bundle') } };
      } else {
        hoodie = { id: this.selectedVariant.id, properties: { ...shared, ...fixedPrice(this.hoodieSetPrice) } };
        addOn = { id: this.addOnVariant.id, properties: { ...shared, ...fixedPrice(this.addOnSetPrice) } };
      }

      Alpine.store('cart').addItems([{ ...hoodie, quantity: 1 }, { ...addOn, quantity: 1 }], () => {
        Alpine.store('cart').isCartInProgress = false;
      });
    },
  }));

  // Full-screen view of the set card photo: close button, tap outside, Esc, or swipe up/down
  Alpine.data('gloImageViewer', () => ({
    open: false,
    // The large photo only loads once the shopper first opens it
    seen: false,
    dragging: false,
    dragY: 0,
    startY: 0,
    lastY: 0,
    lastTime: 0,
    velocity: 0,
    opener: null,

    get backdropStyle() {
      return `opacity:${Math.max(0.2, 1 - Math.abs(this.dragY) / 400)}`;
    },

    show() {
      this.opener = document.activeElement;
      this.dragY = 0;
      this.seen = true;
      this.open = true;
      Alpine.store('screen').isScrollableBody = false;
      this.$nextTick(() => this.$refs.close.focus());
    },

    hide() {
      if (!this.open) return;
      this.open = false;
      this.dragging = false;
      Alpine.store('screen').isScrollableBody = true;
      if (this.opener) this.opener.focus({ preventScroll: true });
    },

    dragStart(event) {
      if (!event.isPrimary) return;
      this.dragging = true;
      this.startY = this.lastY = event.clientY;
      this.lastTime = event.timeStamp;
      this.velocity = 0;
      event.currentTarget.setPointerCapture(event.pointerId);
    },

    dragMove(event) {
      if (!this.dragging) return;
      this.velocity = (event.clientY - this.lastY) / Math.max(event.timeStamp - this.lastTime, 1);
      this.lastY = event.clientY;
      this.lastTime = event.timeStamp;
      this.dragY = event.clientY - this.startY;
    },

    // A long drag, or a flick still moving at release (px/ms), closes; anything shorter springs back
    dragEnd(event) {
      if (!this.dragging) return;
      this.dragging = false;
      const distance = Math.abs(this.dragY);
      const flick = event.timeStamp - this.lastTime < 100 && Math.abs(this.velocity) > 0.5;
      if (distance > 80 || (distance > 30 && flick)) {
        this.dragY = Math.sign(this.dragY) * window.innerHeight;
        this.hide();
      } else {
        this.dragY = 0;
      }
    },
  }));
});
